import React from 'react';
import { Printer, Share2, Bookmark, BookmarkCheck } from 'lucide-react';
import { Recipe } from '../types';

interface RecipeActionsProps {
  recipe: Recipe;
}

function RecipeActions({ recipe }: RecipeActionsProps) {
  const [isSaved, setIsSaved] = React.useState(false);

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    const recipeText = `${recipe.title}\n\n` +
      `Description: ${recipe.description}\n\n` +
      `Ingredients:\n${recipe.ingredients.map(ing => `- ${ing.amount} ${ing.unit} ${ing.name}${ing.notes ? ` (${ing.notes})` : ''}`).join('\n')}\n\n` +
      `Instructions:\n${recipe.instructions.map((step, i) => `${i + 1}. ${step}`).join('\n')}\n\n` +
      `Prep Time: ${recipe.prepTime}\n` +
      `Cook Time: ${recipe.cookTime}\n` +
      `Difficulty: ${recipe.difficulty}\n` +
      `Servings: ${recipe.servings}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: recipe.title,
          text: recipeText,
        });
      } else {
        await navigator.clipboard.writeText(recipeText);
        alert('Recipe copied to clipboard!');
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        alert('Failed to share recipe. Recipe copied to clipboard instead.');
        await navigator.clipboard.writeText(recipeText);
      }
    }
  };

  const handleSave = () => {
    try {
      const savedRecipes = JSON.parse(localStorage.getItem('savedRecipes') || '[]');
      
      if (!isSaved) {
        // Check if recipe already exists to avoid duplicates
        const recipeExists = savedRecipes.some((r: Recipe) => r.title === recipe.title);
        if (!recipeExists) {
          savedRecipes.push(recipe);
          localStorage.setItem('savedRecipes', JSON.stringify(savedRecipes));
          setIsSaved(true);
        }
      } else {
        const filtered = savedRecipes.filter((r: Recipe) => r.title !== recipe.title);
        localStorage.setItem('savedRecipes', JSON.stringify(filtered));
        setIsSaved(false);
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      alert('Failed to save recipe. Please try again.');
    }
  };

  React.useEffect(() => {
    try {
      const savedRecipes = JSON.parse(localStorage.getItem('savedRecipes') || '[]');
      setIsSaved(savedRecipes.some((r: Recipe) => r.title === recipe.title));
    } catch (error) {
      console.error('Error loading saved recipes:', error);
    }
  }, [recipe.title]);

  return (
    <div className="print:hidden flex items-center gap-4 mt-6">
      <button
        onClick={handlePrint}
        className="flex items-center gap-2 px-4 py-2 text-text-light hover:text-primary transition-colors rounded-lg hover:bg-secondary"
      >
        <Printer className="w-5 h-5" />
        <span className="hidden sm:inline">Print Recipe</span>
      </button>

      <button
        onClick={handleShare}
        className="flex items-center gap-2 px-4 py-2 text-text-light hover:text-primary transition-colors rounded-lg hover:bg-secondary"
      >
        <Share2 className="w-5 h-5" />
        <span className="hidden sm:inline">Share</span>
      </button>

      <button
        onClick={handleSave}
        className={`flex items-center gap-2 px-4 py-2 transition-colors rounded-lg ${
          isSaved 
            ? 'text-primary bg-secondary' 
            : 'text-text-light hover:text-primary hover:bg-secondary'
        }`}
      >
        {isSaved ? (
          <BookmarkCheck className="w-5 h-5" />
        ) : (
          <Bookmark className="w-5 h-5" />
        )}
        <span className="hidden sm:inline">{isSaved ? 'Saved' : 'Save'}</span>
      </button>
    </div>
  );
}

export default RecipeActions;